import React, { useEffect, useMemo, useState } from 'react';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, getISOWeek, addDays, startOfWeek, endOfWeek, isSameMonth, isSameDay, isToday, startOfToday, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, ChevronLeft, ChevronRight, Users, Award, GraduationCap, MoreHorizontal, Edit, Trash2, CheckCircle, PlayCircle, Unlock } from 'lucide-react';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { getProjectColor } from '../utils/colors';
import { employeeWorksOnDay, employeeNominalWorkday, employeeUnderContractOn, computeRealEndDate, isStandardWorkday } from '../utils/workDays';
import usePlanningDiagnostics from '../hooks/usePlanningDiagnostics';
import { timeEntryAPI } from '../services/api';
import { formatHours } from '../utils/timeEntries';

const CalendarPlanningView = ({ projects, allProjects = projects, employees, onUpdateProject, onCompleteProject, onEditProject, onDeleteProject, onReleaseProject }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [deleteProjectId, setDeleteProjectId] = useState(null);
  const [draggedProject, setDraggedProject] = useState(null);
  const { alertsByProject } = usePlanningDiagnostics(projects, employees);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Jours passés (jusqu'à hier) : les affectations RÉELLES, tirées des pointages
  // de l'app, à la place du planning. Aujourd'hui reste prévisionnel : ses heures
  // ne sont pas encore toutes saisies.
  const today = startOfToday();
  const realUntil = format(subDays(today, 1), 'yyyy-MM-dd');
  const rangeStart = format(calendarStart, 'yyyy-MM-dd');
  const rangeEnd = format(calendarEnd, 'yyyy-MM-dd');
  const [realEntries, setRealEntries] = useState(null);

  useEffect(() => {
    if (rangeStart > realUntil) {
      setRealEntries([]);
      return undefined;
    }
    let cancelled = false;
    setRealEntries(null);
    timeEntryAPI.list({ since: rangeStart, until: rangeEnd < realUntil ? rangeEnd : realUntil })
      .then((data) => { if (!cancelled) setRealEntries(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setRealEntries([]); });
    return () => { cancelled = true; };
  }, [rangeStart, rangeEnd, realUntil]);

  // Par jour, par chantier : qui a pointé, combien d'heures, sur quelles tâches.
  const realByDay = useMemo(() => {
    const byDay = new Map();
    (realEntries || []).forEach((entry) => {
      if (!byDay.has(entry.date)) byDay.set(entry.date, new Map());
      const cards = byDay.get(entry.date);
      const key = entry.projectId || entry.projectName;
      if (!cards.has(key)) {
        cards.set(key, { key, projectId: entry.projectId, projectName: entry.projectName, hours: 0, people: new Map(), tasks: new Set() });
      }
      const card = cards.get(key);
      const person = card.people.get(entry.employeeId) || { name: entry.employeeFirstName || entry.employeeName, hours: 0 };
      person.hours += entry.hours || 0;
      card.people.set(entry.employeeId, person);
      card.hours += entry.hours || 0;
      if (entry.task) card.tasks.add(entry.task);
    });
    return byDay;
  }, [realEntries]);

  const projectsById = useMemo(() => new Map(allProjects.map((p) => [p.id, p])), [allProjects]);

  const navigateMonth = (direction) => {
    // Toujours partir du 1er du mois : setMonth() sur un 31 déborde sur le
    // mois suivant (31 août + 1 mois = « 31 septembre » = 1er octobre — le
    // Gantt sautait septembre).
    const base = startOfMonth(currentMonth);
    const newMonth = new Date(base);
    newMonth.setMonth(base.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  // Force re-render when month changes
  const monthKey = `${currentMonth.getFullYear()}-${currentMonth.getMonth()}`;

  const handleDeleteProject = () => {
    if (deleteProjectId) {
      onDeleteProject(deleteProjectId);
      setDeleteProjectId(null);
    }
  };

  const getEmployeeById = (id) => employees.find(emp => emp.id === id);

  // Renforts (affectations partielles) réellement présents ce jour-là.
  const renfortsThatDay = (project, day) =>
    (project.extraAssignments || []).filter(a => {
      const from = new Date(a.from); from.setHours(0, 0, 0, 0);
      const to = new Date(a.to); to.setHours(23, 59, 59, 999);
      if (day < from || day > to) return false;
      const emp = getEmployeeById(a.employeeId);
      return emp ? employeeWorksOnDay(emp, day) : false;
    });

  const getProjectsForDay = (day) => {
    return projects
      .filter(project => project.status !== 'completed' && project.actualStartDate)
      .filter(project => {
        const startDate = parseISO(project.actualStartDate);
        const team = (project.assignedEmployees || []).map(getEmployeeById).filter(Boolean);

        let endDate;
        if (project.durationInDays) {
          endDate = computeRealEndDate(startDate, project.durationInDays, team);
        } else {
          endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + (project.duration * 7) - 1);
        }

        if (day < startDate || day > endDate) return false;

        // Dans la plage [début, fin], ne marquer que les jours réellement travaillés
        // par au moins un membre de l'équipe (saute les week-ends, etc.) — ou par
        // un RENFORT : un chantier peut tourner sur ses seuls renforts un jour
        // donné (vécu : Cabaniols les 17-18/09, son noyau se limitant à Kevin,
        // pas encore embauché — le chantier disparaissait du planning et
        // Mohamed et Baba semblaient sans affectation).
        if (team.length > 0) {
          return team.some(emp => employeeWorksOnDay(emp, day))
            || renfortsThatDay(project, day).length > 0;
        }
        // Sans équipe (personne de disponible) : le chantier tourne les jours
        // ouvrés, jamais le week-end ni un jour férié (vécu : un chantier tout
        // juste créé s'affichait samedi et dimanche).
        return isStandardWorkday(day) || renfortsThatDay(project, day).length > 0;
      })
      .sort((a, b) => parseISO(a.actualStartDate) - parseISO(b.actualStartDate));
  };

  const isAnchoredProject = (p) =>
    p.hasFixedStartDate || p.manuallyPlaced || p.status === 'in-progress';

  const projectWindow = (project) => {
    const startDate = parseISO(project.actualStartDate);
    const team = (project.assignedEmployees || []).map(getEmployeeById).filter(Boolean);
    let endDate;
    if (project.durationInDays) {
      endDate = computeRealEndDate(startDate, project.durationInDays, team);
    } else {
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + (project.duration * 7) - 1);
    }
    return { startDate, endDate };
  };

  // Répartition d'effectif : un salarié qu'une ANCRE (date fixe, posé à la
  // main, en cours) réclame ce jour-là est affiché UNIQUEMENT sur l'ancre —
  // pas en doublon sur le chantier flexible qu'il quitte temporairement
  // (le moteur compte déjà son absence dans la fin du flexible).
  const employeeAnchorThatDay = (employeeId, day) => {
    return projects.find(p => {
      if (p.status === 'completed' || !p.actualStartDate || !isAnchoredProject(p)) return false;
      if (!(p.assignedEmployees || []).includes(employeeId)) return false;
      const w = projectWindow(p);
      return day >= w.startDate && day <= w.endDate;
    }) || null;
  };

  const handleCompleteProject = (project) => {
    const completedProject = {
      ...project,
      status: 'completed',
      actualEndDate: new Date().toISOString().split('T')[0]
    };
    onCompleteProject(completedProject);
  };

  const handleStartProject = (project) => {
    onCompleteProject({ ...project, status: 'in-progress' });
  };

  const handleDragStart = (e, project) => {
    if (project.hasFixedStartDate) {
      e.preventDefault();
      return;
    }
    setDraggedProject(project);
    e.dataTransfer.effectAllowed = 'move';
    // Required by Firefox (and some Chromium contexts) for the drag to actually start.
    e.dataTransfer.setData('text/plain', project.id);
  };

  const handleDragEnd = () => {
    setDraggedProject(null);
  };

  const handleDrop = async (e, targetDay) => {
    e.preventDefault();
    if (!draggedProject) return;

    const newStartDate = format(targetDay, 'yyyy-MM-dd');
    
    try {
      // Use the new conflict-resolution API
      const result = await import('../services/api').then(({ projectAPI }) => 
        projectAPI.moveWithConflictResolution(draggedProject.id, newStartDate)
      );
      
      // Update the project with conflict information
      const updatedProject = {
        ...result.project,
        hasConflicts: result.conflictsDetected
      };
      
      onUpdateProject(updatedProject);
      
      // Show appropriate message
      if (result.conflictsDetected) {
        console.warn(`⚠️ ${result.message} - Capacité conservée: ${result.totalWeeklyCapacity}h/semaine`);
      } else {
        console.log(`✅ ${result.message}`);
      }
      
    } catch (error) {
      console.error('Erreur lors du déplacement:', error);
      // Fallback to simple move if API fails
      const updatedProject = {
        ...draggedProject,
        actualStartDate: newStartDate
      };
      onUpdateProject(updatedProject);
    }

    setDraggedProject(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <div key={monthKey} className="bg-white rounded-lg shadow-lg border border-green-100 overflow-hidden">
      {/* En-tête du calendrier */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Calendar className="w-6 h-6" />
            <h2 className="text-xl font-semibold">
              Planning - {format(currentMonth, 'MMMM yyyy', { locale: fr })}
            </h2>
          </div>
          <div className="flex space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateMonth(-1)}
              className="text-white hover:bg-white/20"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost" 
              size="sm"
              onClick={() => setCurrentMonth(new Date())}
              className="text-white hover:bg-white/20 text-sm px-3"
            >
              Aujourd'hui
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateMonth(1)}
              className="text-white hover:bg-white/20"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* En-têtes des jours de la semaine */}
      <div className="grid grid-cols-7 bg-green-100 border-b border-green-200">
        {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'].map((day, index) => (
          <div key={index} className="p-3 text-center font-medium text-green-800 border-r border-green-200 last:border-r-0">
            {day}
          </div>
        ))}
      </div>

      {/* Grille du calendrier */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day, dayIndex) => {
          const isPastDay = day < today;
          const dayProjects = isPastDay ? [] : getProjectsForDay(day);
          const realCards = isPastDay ? [...(realByDay.get(format(day, 'yyyy-MM-dd'))?.values() || [])] : [];
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isCurrentDay = isToday(day);
          // Jours passés en gris : le réel (pointages) se distingue d'un coup d'œil du prévisionnel.
          const cellTone = isCurrentDay ? 'bg-blue-50'
            : isPastDay ? 'bg-gray-100'
              : isCurrentMonth ? 'bg-white' : 'bg-gray-50';
          const weekNumber = getISOWeek(day); // norme ISO-8601 (semaine du lundi), pas la convention US par défaut de date-fns

          return (
            <div
              key={dayIndex}
              className={`min-h-28 border-r border-b border-gray-300 last:border-r-0 p-2 ${cellTone} ${
                isCurrentMonth ? '' : 'text-gray-400'
              } ${draggedProject ? 'transition-colors duration-200' : ''}`}
              onDrop={(e) => handleDrop(e, day)}
              onDragOver={handleDragOver}
              onDragEnter={(e) => {
                if (draggedProject) {
                  e.currentTarget.classList.add('bg-green-100');
                }
              }}
              onDragLeave={(e) => {
                e.currentTarget.classList.remove('bg-green-100');
              }}
            >
              {/* En-tête du jour */}
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${isCurrentDay ? 'text-blue-600' : isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}`}>
                  {format(day, 'd')}
                </span>
                {day.getDay() === 1 && (
                  <span className="text-xs font-medium text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                    S{weekNumber}
                  </span>
                )}
              </div>

              {/* Chantiers du jour */}
              <div className="space-y-1">
                {realCards.map((card) => {
                  const color = getProjectColor(card.projectId || card.key);
                  const project = projectsById.get(card.projectId);
                  const people = [...card.people.values()];
                  return (
                    <div
                      key={card.key}
                      className={`rounded-md p-1.5 text-xs bg-white shadow-sm transition-all duration-200 hover:shadow-md ${project ? 'cursor-pointer' : ''}`}
                      style={{ borderLeft: `3px solid ${color.hex}` }}
                      onClick={(e) => { e.stopPropagation(); if (project && onEditProject) onEditProject(project); }}
                      title={`Pointé : ${people.map((p) => `${p.name} ${formatHours(p.hours)}`).join(', ')}${card.tasks.size ? ` — ${[...card.tasks].join(', ')}` : ''}`}
                    >
                      <div className="font-medium truncate" style={{ color: color.hex }}>✓ {card.projectName}</div>
                      <div className="flex items-center justify-between mt-0.5 space-x-1">
                        <span className="text-gray-600 truncate">
                          {people.length <= 3 ? people.map((p) => p.name).join(' · ') : `${people.length} pers.`}
                        </span>
                        <span className="text-gray-500 tabular-nums shrink-0">{formatHours(card.hours)}</span>
                      </div>
                    </div>
                  );
                })}
                {isPastDay && realEntries && realCards.length === 0
                  && isStandardWorkday(day) && (
                  <div className="text-xs text-gray-500 italic">Aucun pointage</div>
                )}

                {dayProjects.map((project) => {
                  const color = getProjectColor(project.id);
                  // Équipe EFFECTIVE ce jour : les membres partis sur une ancre
                  // ce jour-là n'apparaissent que sur l'étiquette de l'ancre.
                  // ... et seulement ceux qui travaillent VRAIMENT ce jour-là :
                  // sans ce filtre, un membre du noyau en congé, en formation ou
                  // pas encore embauché s'affichait comme présent (vécu : Kevin
                  // annoncé sur Cabaniols mi-septembre, son contrat démarrant
                  // le 28).
                  const assignedEmployees = (project.assignedEmployees || []).filter(empId => {
                    const anchor = employeeAnchorThatDay(empId, day);
                    if (anchor && anchor.id !== project.id) return false;
                    const emp = getEmployeeById(empId);
                    return emp ? employeeWorksOnDay(emp, day) : false;
                  });
                  // Renforts (bouche-trous) actifs ce jour précis : affectations
                  // partielles, distinctes du noyau présent du début à la fin
                  const dayRenforts = renfortsThatDay(project, day);
                  // Personnel qui manque ce jour pour tenir l'effectif minimum, affiché
                  // « +n personnel » à la place d'un nom. Chantier en conflit ou sans
                  // équipe : tout ce qui n'est pas là. Sinon, seule une absence
                  // exceptionnelle compte : ni un jour de repos habituel, ni un salarié
                  // prêté à un chantier ancré, que le moteur a déjà intégrés.
                  const minEmployees = project.minEmployees || 1;
                  const core = (project.assignedEmployees || []).map(getEmployeeById).filter(Boolean);
                  let missingThatDay = 0;
                  if (project.hasConflicts || core.length === 0) {
                    missingThatDay = Math.max(0, minEmployees - assignedEmployees.length - dayRenforts.length);
                  } else {
                    const onsite = core.filter((emp) => employeeWorksOnDay(emp, day)).length + dayRenforts.length;
                    const usual = core.filter((emp) => employeeNominalWorkday(emp, day) && employeeUnderContractOn(emp, day)).length;
                    if (usual >= minEmployees && onsite < minEmployees) missingThatDay = minEmployees - onsite;
                  }
                  const assignmentAlert = (alertsByProject[project.id] || []).find((a) => a.label === "Conflit d'assignation");
                  
                  return (
                    <div
                      key={project.id}
                      className={`relative group rounded-md p-1.5 text-xs cursor-pointer transition-all duration-200 hover:shadow-md ${
                        project.hasFixedStartDate ? 'cursor-not-allowed opacity-75' : 'cursor-move'
                      } ${draggedProject?.id === project.id ? 'opacity-50 scale-95' : ''}`}
                      style={{ 
                        backgroundColor: color.hex + '15', // Reduced opacity for performance
                        borderLeft: `3px solid ${color.hex}`
                      }}
                      draggable={!project.hasFixedStartDate}
                      onDragStart={(e) => handleDragStart(e, project)}
                      onDragEnd={handleDragEnd}
                      onClick={(e) => { e.stopPropagation(); onEditProject && onEditProject(project); }}
                      title="Cliquer pour ouvrir la fiche du chantier"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate text-xs" style={{ color: color.hex }}>
                            {project.isUrgent && <span title="Chantier urgent">🔥</span>}
                            {project.deadlineExceeded && <span title={`Deadline dépassée (à terminer avant le ${project.deadline})`}>⏰</span>}
                            {project.advancedFromMonth && <span title={`Avancé — mois souhaité : ${project.advancedFromMonth} (pour occuper l'équipe)`}>⏩</span>}
                            {project.name}
                          </div>
                          {assignmentAlert && missingThatDay === 0 && (
                            <div className="flex items-center mt-0.5 space-x-1 text-orange-700" title={assignmentAlert.detail}>
                              <span>⚠️</span>
                              <span className="text-xs font-medium">Conflit d'assignation</span>
                            </div>
                          )}
                          {(
                            <>
                              {(assignedEmployees.length > 0 || missingThatDay > 0) && (
                                <div className="flex flex-wrap items-center mt-0.5 gap-x-1">
                                  <Users className="w-2 h-2 text-gray-500" />
                                  {assignedEmployees.length > 3 ? (
                                    <span className="text-xs text-gray-600">{assignedEmployees.length} pers.</span>
                                  ) : assignedEmployees.map(empId => {
                                    const employee = getEmployeeById(empId);
                                    if (!employee) return null;
                                    return (
                                      <div key={empId} className="flex items-center">
                                        <span className="text-xs text-gray-600 truncate max-w-12">
                                          {employee.firstName}
                                        </span>
                                        {employee.isQualified && <span className="text-blue-500 ml-0.5">★</span>}
                                        {employee.isApprentice && <span className="text-yellow-500 ml-0.5">🎓</span>}
                                      </div>
                                    );
                                  })}
                                  {missingThatDay > 0 && (
                                    <span className="text-xs font-semibold text-orange-700 whitespace-nowrap"
                                          title="Personnel ponctuel à trouver ce jour (intérim, renfort) pour tenir l'effectif minimum">
                                      +{missingThatDay} personnel
                                    </span>
                                  )}
                                </div>
                              )}
                              {dayRenforts.length > 0 && (
                                <div className="flex items-center mt-0.5 space-x-1" title="Renfort ce jour (affectation partielle)">
                                  <div className="flex space-x-0.5">
                                    {dayRenforts.map(a => {
                                      const employee = getEmployeeById(a.employeeId);
                                      if (!employee) return null;
                                      return (
                                        <span key={a.employeeId} className="text-xs text-purple-600 italic truncate max-w-14">
                                          +{employee.firstName}
                                          {employee.isApprentice && '🎓'}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Menu d'actions - simplifié pour performance */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-3 w-3 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <MoreHorizontal className="w-2 h-2" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onEditProject(project)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Modifier
                            </DropdownMenuItem>
                            {project.status === 'planned' && (
                              <DropdownMenuItem onClick={() => handleStartProject(project)}>
                                <PlayCircle className="w-4 h-4 mr-2" />
                                Marquer en cours
                              </DropdownMenuItem>
                            )}
                            {(project.manuallyPlaced || project.manualAssignment) && (
                              <DropdownMenuItem onClick={() => onReleaseProject(project)}>
                                <Unlock className="w-4 h-4 mr-2" />
                                Libérer (replanification auto)
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleCompleteProject(project)}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Marquer terminé
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => setDeleteProjectId(project.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Indicateurs compacts */}
                      <div className="flex items-center justify-between mt-0.5">
                        <div className="flex items-center space-x-1">
                          {project.isComplex && (
                            <span className="w-1 h-1 bg-orange-500 rounded-full" title="Complexe"></span>
                          )}
                          {project.hasFixedStartDate && (
                            <span className="text-red-500 text-xs">📌</span>
                          )}
                          {project.manuallyPlaced && !project.hasFixedStartDate && (
                            <span className="text-xs" title="Posé à la main — non replanifié automatiquement (menu ⋯ > Libérer)">📍</span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">
                          {project.duration}s
                        </span>
                      </div>
                    </div>
                  );
                })}

              </div>
            </div>
          );
        })}
      </div>

      {/* Dialog de confirmation de suppression */}
      <AlertDialog open={deleteProjectId !== null} onOpenChange={() => setDeleteProjectId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le chantier</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce chantier ? Cette action est irréversible.
              {deleteProjectId && (
                <span className="block mt-2 font-medium">
                  {projects.find(p => p.id === deleteProjectId)?.name}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteProject} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CalendarPlanningView;